import { Fragment, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { LogIn, Menu, Search, User, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import {
  buildAuthHref,
  getAccountActions,
  resolveDisplayName,
} from './auth-menu-actions'
import { AuthButton } from './auth-button'
import { BrandLogo } from './brand-logo'
import { CartIcon } from './cart/cart-icon'

export function SiteHeader() {
  const { user, isAdmin, adminUser, customerProfile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const navLinks = [
    { href: '/', label: 'This Week' },
    { href: '/past-reviews', label: 'Past Reviews' },
    { href: '/about', label: 'About' },
  ]

  const resolvedDisplayName = resolveDisplayName(
    adminUser?.name || customerProfile?.display_name,
    user?.email
  )
  const accountActions = getAccountActions(isAdmin)

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setIsMobileMenuOpen(false)
    setIsSearchOpen(false)
    navigate('/')
  }

  const signInHref = buildAuthHref(location.pathname, searchParams, 'signin')
  const signUpHref = buildAuthHref(location.pathname, searchParams, 'signup')

  return (
    <header className="sticky top-0 z-40 w-full border-b border-zinc-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="container mx-auto px-4">
        <div className="hidden h-16 items-center justify-between md:flex">
          <Link to="/" className="flex items-center">
            <BrandLogo size="lg" className="size-14" />
          </Link>

          <nav className="flex items-center gap-6">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-900"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Sheet open={isSearchOpen} onOpenChange={setIsSearchOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-zinc-600 hover:text-zinc-900"
                >
                  <Search className="size-5" />
                  <span className="sr-only">Open search</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="top"
                className="h-auto border-x-0 border-b bg-white p-0"
              >
                <SheetHeader className="border-b border-zinc-200">
                  <SheetTitle>Search</SheetTitle>
                  <SheetDescription>
                    Search reviews and products. Full search is coming soon.
                  </SheetDescription>
                </SheetHeader>
                <div className="p-4">
                  <div className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2">
                    <Search className="size-4 text-zinc-500" />
                    <input
                      aria-label="Search coming soon"
                      value=""
                      readOnly
                      placeholder="Search coming soon"
                      className="w-full bg-transparent text-sm text-zinc-500 outline-none"
                    />
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <AuthButton
              user={user}
              isAdmin={isAdmin}
              displayName={adminUser?.name || customerProfile?.display_name || null}
            />
            <CartIcon />
          </div>
        </div>

        <div className="relative flex h-16 items-center justify-between md:hidden">
          <div className="z-10 flex items-center gap-1">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-zinc-600 hover:text-zinc-900"
                >
                  <Menu className="size-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[18rem] max-w-[85vw] bg-white p-0">
                <SheetHeader className="border-b border-zinc-200">
                  <SheetTitle>Menu</SheetTitle>
                  <SheetDescription className="sr-only">
                    Primary site navigation
                  </SheetDescription>
                </SheetHeader>
                <nav className="px-4 py-6">
                  <ul className="space-y-1">
                    {navLinks.map(link => (
                      <li key={link.href}>
                        <Link
                          to={link.href}
                          onClick={closeMobileMenu}
                          className="block rounded-md px-3 py-2 text-base font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </SheetContent>
            </Sheet>

            <Sheet open={isSearchOpen} onOpenChange={setIsSearchOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-zinc-600 hover:text-zinc-900"
                >
                  <Search className="size-5" />
                  <span className="sr-only">Open search</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="top"
                className="h-auto border-x-0 border-b bg-white p-0"
              >
                <SheetHeader className="border-b border-zinc-200">
                  <SheetTitle>Search</SheetTitle>
                  <SheetDescription>
                    Search reviews and products. Full search is coming soon.
                  </SheetDescription>
                </SheetHeader>
                <div className="p-4">
                  <div className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2">
                    <Search className="size-4 text-zinc-500" />
                    <input
                      aria-label="Search coming soon"
                      value=""
                      readOnly
                      placeholder="Search coming soon"
                      className="w-full bg-transparent text-sm text-zinc-500 outline-none"
                    />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <Link to="/" className="absolute left-1/2 -translate-x-1/2 px-2">
            <BrandLogo size="lg" className="size-12" />
          </Link>

          <div className="z-10 flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-zinc-600 hover:text-zinc-900"
                >
                  <User className="size-5" />
                  <span className="sr-only">Open account menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {user ? (
                  <>
                    <div className="px-2 py-1.5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Account</p>
                      <p className="mt-1 truncate text-sm font-semibold text-zinc-900">{resolvedDisplayName}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {accountActions.map(action => (
                      <Fragment key={action.key}>
                        {action.separatorBefore && <DropdownMenuSeparator />}
                        {action.actionType === 'navigate' ? (
                          <DropdownMenuItem asChild>
                            <Link to={action.href} className="cursor-pointer">
                              <action.icon className="mr-2 h-4 w-4" />
                              {action.label}
                            </Link>
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={handleLogout}
                            className={action.destructive ? 'cursor-pointer text-red-600' : 'cursor-pointer'}
                          >
                            <action.icon className="mr-2 h-4 w-4" />
                            {action.label}
                          </DropdownMenuItem>
                        )}
                        {action.separatorAfter && <DropdownMenuSeparator />}
                      </Fragment>
                    ))}
                  </>
                ) : (
                  <>
                    <DropdownMenuItem asChild>
                      <Link to={signInHref} className="cursor-pointer">
                        <LogIn className="mr-2 h-4 w-4" />
                        Sign In
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to={signUpHref} className="cursor-pointer">
                        <UserPlus className="mr-2 h-4 w-4" />
                        Create Account
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <CartIcon />
          </div>
        </div>
      </div>
    </header>
  )
}
